import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Pie } from 'react-chartjs-2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCreditCard, faDollarSign, faPercentage, faChartLine, faQuestionCircle, faLightbulb, faStar } from '@fortawesome/free-solid-svg-icons';

const PayPalFeeCalculator = () => {
  const [amount, setAmount] = useState('');
  const [country, setCountry] = useState('US');
  const [currency, setCurrency] = useState('USD');
  const [fixedFee, setFixedFee] = useState(0.30);
  const [percentageFee, setPercentageFee] = useState(2.9);
  const [totalFee, setTotalFee] = useState('');
  const [netAmount, setNetAmount] = useState('');
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const countryFees = {
    US: { fixed: 0.30, percentage: 2.9 },
    UK: { fixed: 0.20, percentage: 2.9 },
    CA: { fixed: 0.30, percentage: 2.9 },
    AU: { fixed: 0.30, percentage: 2.9 },
    EU: { fixed: 0.35, percentage: 2.9 },
    IN: { fixed: 3.00, percentage: 2.9 }, // India fees
    SG: { fixed: 0.30, percentage: 2.9 }, // Singapore
    MY: { fixed: 0.30, percentage: 2.9 }, // Malaysia
    TH: { fixed: 0.30, percentage: 2.9 }, // Thailand
    PH: { fixed: 0.30, percentage: 2.9 }  // Philippines
  };

  const calculateFee = () => {
    try {
      const amountValue = parseFloat(amount);
      
      if (isNaN(amountValue) || amountValue <= 0) {
        throw new Error('Please enter a valid amount');
      }

      // Get country-specific fees
      const { fixed: countryFixed, percentage: countryPercentage } = countryFees[country];
      setFixedFee(countryFixed);
      setPercentageFee(countryPercentage);

      // Calculate fees
      const fixed = countryFixed;
      const percentage = (amountValue * countryPercentage) / 100;
      const total = fixed + percentage;
      const net = amountValue - total;

      // Format results
      setTotalFee(total.toFixed(2));
      setNetAmount(net.toFixed(2));
      setError('');

      // Update chart data
      setChartData({
        labels: ['Fixed Fee', 'Percentage Fee', 'Net Amount'],
        datasets: [{
          data: [fixed, percentage, net],
          backgroundColor: [
            'rgb(255, 99, 132)',
            'rgb(54, 162, 235)',
            'rgb(75, 192, 192)'
          ],
          hoverOffset: 4
        }]
      });
    } catch (err) {
      setError(err.message);
      setTotalFee('');
      setNetAmount('');
      setChartData(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 to-blue-100">
      <Helmet>
        <title>PayPal Fee Calculator</title>
        <meta name="description" content="Calculate PayPal transaction fees and net amount. Supports multiple currencies and countries." />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <FontAwesomeIcon 
            icon={faCreditCard} 
            className="text-4xl text-blue-600 mb-4" 
          />
          <h1 className="text-4xl font-bold text-gray-800 mb-2">PayPal Fee Calculator</h1>
          <p className="text-xl text-gray-600">Calculate your PayPal transaction fees and net amount</p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Transaction Amount</label>
              <div className="flex">
                <select
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  className="w-20 border-gray-300 rounded-l-md focus:border-blue-500 focus:ring-blue-500"
                >
                  <option value="USD">$ USD</option>
                  <option value="EUR">€ EUR</option>
                  <option value="GBP">£ GBP</option>
                  <option value="AUD">A$ AUD</option>
                  <option value="CAD">C$ CAD</option>
                  <option value="INR">₹ INR</option>
                  <option value="SGD">S$ SGD</option>
                  <option value="MYR">RM MYR</option>
                  <option value="THB">฿ THB</option>
                  <option value="PHP">₱ PHP</option>
                </select>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="flex-1 border-gray-300 rounded-r-md focus:border-blue-500 focus:ring-blue-500"
                  placeholder="Enter amount"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
              <select
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="US">United States</option>
                <option value="UK">United Kingdom</option>
                <option value="CA">Canada</option>
                <option value="AU">Australia</option>
                <option value="EU">European Union</option>
                <option value="IN">India</option>
                <option value="SG">Singapore</option>
                <option value="MY">Malaysia</option>
                <option value="TH">Thailand</option>
                <option value="PH">Philippines</option>
              </select>
            </div>

            <div className="mt-6">
              <button
                onClick={calculateFee}
                className="w-full bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Calculate Fees
              </button>
            </div>

            {error && (
              <div className="mt-4 p-4 bg-red-100 text-red-700 rounded-md">
                {error}
              </div>
            )}

            {totalFee && (
              <div className="mt-8">
                <h2 className="text-xl font-semibold mb-4 text-blue-600">Results</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <div className="flex items-center mb-2">
                      <FontAwesomeIcon icon={faDollarSign} className="text-blue-600 mr-3" />
                      <h3 className="text-lg font-semibold text-gray-700">Total PayPal Fee</h3>
                    </div>
                    <p className="text-2xl font-bold text-blue-600">${totalFee}</p>
                  </div>

                  <div className="bg-gray-50 p-6 rounded-lg">
                    <div className="flex items-center mb-2">
                      <FontAwesomeIcon icon={faPercentage} className="text-blue-600 mr-3" />
                      <h3 className="text-lg font-semibold text-gray-700">Fixed Fee</h3>
                    </div>
                    <p className="text-2xl font-bold text-blue-600">${fixedFee}</p>
                  </div>

                  <div className="bg-gray-50 p-6 rounded-lg">
                    <div className="flex items-center mb-2">
                      <FontAwesomeIcon icon={faPercentage} className="text-blue-600 mr-3" />
                      <h3 className="text-lg font-semibold text-gray-700">Percentage Fee</h3>
                    </div>
                    <p className="text-2xl font-bold text-blue-600">{percentageFee}%</p>
                  </div>

                  <div className="bg-gray-50 p-6 rounded-lg">
                    <div className="flex items-center mb-2">
                      <FontAwesomeIcon icon={faDollarSign} className="text-blue-600 mr-3" />
                      <h3 className="text-lg font-semibold text-gray-700">Net Amount</h3>
                    </div>
                    <p className="text-2xl font-bold text-blue-600">${netAmount}</p>
                  </div>
                </div>

                {chartData && (
                  <div className="mt-8">
                    <h2 className="text-xl font-semibold mb-4 text-blue-600">Fee Breakdown</h2>
                    <div className="bg-white p-6 rounded-lg shadow-md">
                      <div className="bg-gray-50 p-4 rounded-lg mb-4">
                        <p className="text-sm text-gray-600">This pie chart shows the breakdown of your fees and net amount.</p>
                      </div>
                      <div className="relative h-[300px]">
                        <Pie
                          data={chartData}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                              legend: {
                                position: 'top',
                              },
                              title: {
                                display: true,
                                text: 'Fee Distribution'
                              }
                            }
                          }}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="mt-12">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4 text-blue-600">Features & Benefits</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-center">
                <FontAwesomeIcon icon={faLightbulb} className="text-yellow-500 mr-4" />
                <div>
                  <h3 className="font-semibold text-gray-700">Multiple Currency Support</h3>
                  <p className="text-gray-600">Supports major currencies including USD, EUR, GBP, and Indian Rupee (INR)</p>
                </div>
              </div>
              <div className="flex items-center">
                <FontAwesomeIcon icon={faChartLine} className="text-blue-500 mr-4" />
                <div>
                  <h3 className="font-semibold text-gray-700">Visual Fee Breakdown</h3>
                  <p className="text-gray-600">See exactly how your fees are distributed with our pie chart visualization</p>
                </div>
              </div>
              <div className="flex items-center">
                <FontAwesomeIcon icon={faQuestionCircle} className="text-green-500 mr-4" />
                <div>
                  <h3 className="font-semibold text-gray-700">Country-specific Fees</h3>
                  <p className="text-gray-600">Get accurate calculations with country-specific fee structures</p>
                </div>
              </div>
              <div className="flex items-center">
                <FontAwesomeIcon icon={faStar} className="text-purple-500 mr-4" />
                <div>
                  <h3 className="font-semibold text-gray-700">Real-time Calculations</h3>
                  <p className="text-gray-600">Get instant results as you input your values</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4 text-blue-600">How to Use</h2>
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-gray-700">Step 1: Enter Transaction Details</h3>
                <ol className="list-decimal pl-6 mt-2 space-y-2">
                  <li>Select your transaction currency (e.g., USD, INR)</li>
                  <li>Enter the transaction amount</li>
                  <li>Select your country for accurate fee calculation</li>
                </ol>
              </div>

              <div>
                <h3 className="font-semibold text-gray-700">Step 2: Calculate Fees</h3>
                <ol className="list-decimal pl-6 mt-2 space-y-2">
                  <li>Click the "Calculate Fees" button</li>
                  <li>View your total PayPal fee, fixed fee, and percentage fee</li>
                  <li>Check your net amount after fees</li>
                </ol>
              </div>

              <div>
                <h3 className="font-semibold text-gray-700">Step 3: Analyze Results</h3>
                <ol className="list-decimal pl-6 mt-2 space-y-2">
                  <li>Review the pie chart to see fee distribution</li>
                  <li>Compare different countries' fee structures</li>
                  <li>Plan your transactions based on fee calculations</li>
                </ol>
              </div>
            </div>
          </div>

          <div className="mt-8 bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4 text-blue-600">How PayPal Fees Work</h2>
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-gray-700">Standard Fees</h3>
                <p className="text-gray-600">PayPal charges a fixed fee plus a percentage of the transaction amount.</p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-700">Fixed Fee</h3>
                <p className="text-gray-600">A small fixed fee is charged per transaction. This varies by country.</p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-700">Percentage Fee</h3>
                <p className="text-gray-600">A percentage of the transaction amount is charged as a fee. This is typically 2.9%.</p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-700">Country Variations</h3>
                <p className="text-gray-600">Fees can vary significantly between countries. For example, India has a higher fixed fee of ₹3.00.</p>
              </div>
            </div>
          </div>

          <div className="mt-8 bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4 text-blue-600">FAQ</h2>
            <div className="space-y-6">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-700 mb-2">What are PayPal fees?</h3>
                <p className="text-gray-600">PayPal charges fees for processing transactions. The fees include a fixed amount and a percentage of the transaction amount.</p>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-700 mb-2">How are fees calculated?</h3>
                <p className="text-gray-600">Fees are calculated by adding a fixed fee to a percentage of the transaction amount. The total fee is then subtracted from the transaction amount to get the net amount.</p>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-700 mb-2">Why do fees vary by country?</h3>
                <p className="text-gray-600">PayPal charges different fees in different countries due to varying regulations and operating costs. For example, India has a higher fixed fee of ₹3.00.</p>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-700 mb-2">How accurate are the calculations?</h3>
                <p className="text-gray-600">The calculator uses PayPal's standard fee structure. However, actual fees might vary based on specific transaction conditions or promotions.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PayPalFeeCalculator;
